import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiDelete, apiPatch, apiPost, operationsClient } from '@/lib/api/client';
import { endpointsForWebhookSurface, type WebhookSubscriptionSurface } from '@/lib/api/endpoints';
import { webhookSubscriptionKeys } from '@/lib/queryKeys';
import type {
  CreateWebhookSubscriptionDto,
  SetEnabledWebhookSubscriptionDto,
  UpdateWebhookSubscriptionDto,
  WebhookDeliveryView,
  WebhookSubscriptionView,
} from '@/types/webhook-subscription';

/**
 * Create a subscription. The response contains the cleartext signing secret in
 * `secret`; the UI must surface it once for the contractor to copy and never
 * show it again.
 */
export function useCreateWebhookSubscription(surface: WebhookSubscriptionSurface = 'generic') {
  const queryClient = useQueryClient();
  const endpoints = endpointsForWebhookSurface(surface);
  return useApiMutation<WebhookSubscriptionView, CreateWebhookSubscriptionDto>({
    mutationFn: (dto) =>
      apiPost<WebhookSubscriptionView, CreateWebhookSubscriptionDto>(
        operationsClient,
        endpoints.create(),
        dto,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: webhookSubscriptionKeys.lists(surface),
      });
      toast.success('Webhook subscription created');
    },
  });
}

interface UpdateWebhookVariables extends UpdateWebhookSubscriptionDto {
  id: string;
}

/**
 * Update mutable subscription fields.
 */
export function useUpdateWebhookSubscription(surface: WebhookSubscriptionSurface = 'generic') {
  const queryClient = useQueryClient();
  const endpoints = endpointsForWebhookSurface(surface);
  return useApiMutation<WebhookSubscriptionView, UpdateWebhookVariables>({
    mutationFn: ({ id, ...dto }) =>
      apiPatch<WebhookSubscriptionView, UpdateWebhookSubscriptionDto>(
        operationsClient,
        endpoints.update(id),
        dto,
      ),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({
        queryKey: webhookSubscriptionKeys.lists(surface),
      });
      void queryClient.invalidateQueries({
        queryKey: webhookSubscriptionKeys.detail(id, surface),
      });
      toast.success('Webhook updated');
    },
  });
}

interface SetEnabledVariables extends SetEnabledWebhookSubscriptionDto {
  id: string;
}

/**
 * Quick enable/disable toggle.
 */
export function useSetWebhookEnabled(surface: WebhookSubscriptionSurface = 'generic') {
  const queryClient = useQueryClient();
  const endpoints = endpointsForWebhookSurface(surface);
  return useApiMutation<WebhookSubscriptionView, SetEnabledVariables>({
    mutationFn: ({ id, enabled }) =>
      apiPatch<WebhookSubscriptionView, SetEnabledWebhookSubscriptionDto>(
        operationsClient,
        endpoints.setEnabled(id),
        { enabled },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: webhookSubscriptionKeys.lists(surface),
      });
    },
  });
}

export function useTestWebhookDelivery(surface: WebhookSubscriptionSurface = 'generic') {
  const queryClient = useQueryClient();
  const endpoints = endpointsForWebhookSurface(surface);
  return useApiMutation<WebhookDeliveryView, { id: string; eventType?: string }>({
    mutationFn: ({ id, eventType }) =>
      apiPost<WebhookDeliveryView, { eventType?: string }>(
        operationsClient,
        endpoints.test(id),
        eventType ? { eventType } : {},
      ),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({
        queryKey: webhookSubscriptionKeys.detail(id, surface),
      });
      void queryClient.invalidateQueries({
        queryKey: [...webhookSubscriptionKeys.detail(id, surface), 'deliveries'],
      });
      toast.success('Test delivery queued');
    },
  });
}

export function useRotateWebhookSecret(surface: WebhookSubscriptionSurface = 'generic') {
  const queryClient = useQueryClient();
  const endpoints = endpointsForWebhookSurface(surface);
  return useApiMutation<WebhookSubscriptionView, string>({
    mutationFn: (id) =>
      apiPost<WebhookSubscriptionView, Record<string, never>>(
        operationsClient,
        endpoints.rotateSecret(id),
        {},
      ),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({
        queryKey: webhookSubscriptionKeys.lists(surface),
      });
      void queryClient.invalidateQueries({
        queryKey: webhookSubscriptionKeys.detail(id, surface),
      });
      toast.success('Webhook secret rotated');
    },
  });
}

export function useRetryWebhookDelivery(surface: WebhookSubscriptionSurface = 'generic') {
  const queryClient = useQueryClient();
  const endpoints = endpointsForWebhookSurface(surface);
  return useApiMutation<WebhookDeliveryView, { id: string; deliveryId: string }>({
    mutationFn: ({ id, deliveryId }) =>
      apiPost<WebhookDeliveryView, Record<string, never>>(
        operationsClient,
        endpoints.retryDelivery(id, deliveryId),
        {},
      ),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({
        queryKey: [...webhookSubscriptionKeys.detail(id, surface), 'deliveries'],
      });
      toast.success('Delivery retry queued');
    },
  });
}

/**
 * Delete a subscription.
 */
export function useDeleteWebhookSubscription(surface: WebhookSubscriptionSurface = 'generic') {
  const queryClient = useQueryClient();
  const endpoints = endpointsForWebhookSurface(surface);
  return useApiMutation<void, string>({
    mutationFn: (id) => apiDelete(operationsClient, endpoints.delete(id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: webhookSubscriptionKeys.lists(surface),
      });
      toast.success('Webhook deleted');
    },
  });
}
